import { CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Row, Space } from 'antd';
import {
  message as showMessage,
  ModalDialog,
  DataGridPlus,
  DeleteButton,
  TablesFilter,
} from 'components/common';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSortAreas, delSortAreas } from 'services/warehouse';
import AddEditDialog from './AddEditDialog';

type DialogProps = {
  warehouse: WarehouseRow;
  onClose: () => void;
  onRefresh: () => void;
  visible: boolean;
};

const SortAreasDialog = (props: DialogProps) => {
  const { onClose, visible, warehouse } = props;
  const [detailDialogIsFullscreen, setDetailDialogIsFullscreen] =
    useState<boolean>(false);
  const refreshBtnRef = useRef<any>(null);
  const [inited, setInited] = useState(false);
  const [AddEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const [current, setCurrent] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [sourceData, setSourceDate] = useState<any[]>([]);
  const [filteredData, setFilteredData] = React.useState<any[]>([]);

  const detailDialogWidth = useCallback(
    (isFullscreen = detailDialogIsFullscreen) => {
      return isFullscreen
        ? window.innerWidth - 4
        : window.innerWidth > 1280
        ? window.innerWidth * 0.8
        : 1200;
    },
    [detailDialogIsFullscreen]
  );

  const getDialogDom = () => {
    const refreshBtn = refreshBtnRef?.current;

    if (refreshBtn) {
      return refreshBtn.parentNode.parentNode.parentNode.parentNode.parentNode
        .parentNode.parentNode;
    }

    return null;
  };

  const onFullscreen = (isFullscreen: boolean) => {
    const dialog = getDialogDom();

    setDetailDialogIsFullscreen(isFullscreen);

    if (dialog) {
      const width = detailDialogWidth(isFullscreen);

      dialog.style.width = `${width}px`;
    }
  };

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSortAreas(warehouse.warehouseId);
      setLoading(false);
      if (res.isSuccess) {
        setSourceDate(res.data);
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  },[warehouse.warehouseId]);

  const delLine = useCallback(async (line: any) => {
    setLoading(true);
    try {
      const res = await delSortAreas(line.sortAreaNum);
      if (res.isSuccess) {
        showMessage.success('deleted successfully');
        getList();
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  },[getList]);

  const columns = [
    {
      name: 'sortAreaNum',
      header: 'ID',
      defaultVisible: false,
    },
    {
      name: 'sortAreaCode',
      header: 'Code',
      userSelect: true,
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: 'name',
      header: 'Name',
      userSelect: true,
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: 'ratio',
      header: 'Ratio',
      userSelect: true,
      defaultFlex: 1,
      sortable: true,
    },
    {
      name: 'action',
      header: 'Action',
      showColumnMenuTool: false,
      groupBy: false,
      sortable: false,
      autoWidth: false,
      minWidth: 250,
      textAlign: 'center',
      render: (value: any) => {
        const { data } = value;
        return (
          <Space>
            <Button
              onClick={() => {
                setAddEditDialogVisible(true);
                setCurrent(data);
              }}
            >
              <EditOutlined />
              Edit
            </Button>
            <DeleteButton
              confirmContent={`name: ${data.name}`}
              confirmTitle="Are you sure you want to delete the line ?"
              onClick={() => delLine(data)}
            />
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    if (!inited) {
      getList();
      setInited(true);
    }
  }, [getList, inited]);

  return (
    <ModalDialog
      centered
      className="fullscreen-modal"
      closable={false}
      closeButton={true}
      // footer={null}
      fullscreen={true}
      maskClosable={false}
      onClose={onClose}
      onFullscreen={onFullscreen}
      title={`SortArea Management - ${warehouse.warehouseCode}`}
      visible={visible}
      width={detailDialogWidth()}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose} icon={<CloseOutlined />}>
              Close
            </Button>
          </Space>
        </Row>
      }
    >
      <Row justify="space-between">
        <TablesFilter
          columns={columns}
          dataSource={sourceData}
          setFilteredData={setFilteredData}
          width={200}
        />
        <Button
          type="default"
          onClick={() => {
            setCurrent(undefined);
            setAddEditDialogVisible(true);
          }}
          icon={<PlusOutlined />}
        >
          Add SortArea
        </Button>
      </Row>

      <DataGridPlus
        style={{ minHeight: 500 }}
        idProperty="id"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination="local"
        {...({} as any)}
      />
      {AddEditDialogVisible && (
        <AddEditDialog
          visible
          onHide={() => setAddEditDialogVisible(false)}
          onRefresh={() => {
            setAddEditDialogVisible(false);
            getList();
          }}
          sortArea={current}
          warehouse={warehouse}
        />
      )}
    </ModalDialog>
  );
};

export default SortAreasDialog;
